/**
 * useDriveModeState — local, consent-based moving_state source.
 *
 * Stage-2 P1 follow-up — Drive Mode P1.1 (Feb 18, 2026).
 *
 * Doctrine:
 *   • Single human-driven source of truth for `moving_state`.
 *   • Persisted in localStorage ONLY — never written to the
 *     server, never sent in analytics, never observed without
 *     operator action. This is consent-based architecture.
 *   • Default `unknown` (safe baseline). The hook never invents
 *     `parked` optimistically.
 *   • Invalid persisted values fail closed to `unknown` via the
 *     shared rule layer.
 *   • Cross-tab sync via the `storage` event so a toggle in one
 *     tab reaches sibling tabs without forcing a poll.
 *
 * P1.1 — auto-clear timer:
 *   Payload upgraded from a plain string to a small JSON envelope
 *   { v: 1, moving_state, updated_at }. On read, any payload whose
 *   `updated_at` is older than `STALE_TTL_MS` (4 hours) fails
 *   closed to `unknown`. Legacy plain-string values from the v0
 *   payload are treated as undated → also fail closed to
 *   `unknown` so yesterday's "I'm parked" can never unlock
 *   today's first lesson.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { normaliseMovingState } from "../lib/driveMode";

const STORAGE_KEY = "ndg_drive_mode_v1";
const PAYLOAD_VERSION = 1;
// Operator-locked window. A doctrinal change here must come with
// a test update — the value is pinned in the regression suite.
const STALE_TTL_MS = 4 * 60 * 60 * 1000;

// Same-tab propagation channel — Feb 19, 2026 P2 fix.
// The W3C `storage` event only fires in OTHER tabs/windows of the
// same origin. So when DriveModeToggle writes, sibling hook
// instances in the SAME tab (CompetencyGrowthSidebar, WorkMode-
// ReturnCard, GoalOutcomeCard, ExposureProgressionPanel) would
// otherwise wait up to 60s for the staleness sweep before seeing
// the change. Dispatching a window-level CustomEvent on every
// write lets every other hook instance re-resolve in the same
// microtask.
const SAME_TAB_EVENT = "ndg:drive-mode-changed";

function _now() { return Date.now(); }

function _isValidTimestamp(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}


/**
 * Parse the raw localStorage value into a calm-baseline state.
 *
 * Returns `unknown` for:
 *   • absent key
 *   • plain-string legacy values (v0 — never had a timestamp)
 *   • malformed JSON
 *   • payload missing/invalid moving_state
 *   • payload missing/invalid updated_at
 *   • payload older than STALE_TTL_MS
 */
function _parsePersisted(raw, nowMs) {
  if (raw === null || raw === undefined || raw === "") return "unknown";
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    // Legacy v0 plain-string value (e.g. "parked") OR genuinely
    // corrupt JSON. Either way: fail closed.
    return "unknown";
  }
  if (!parsed || typeof parsed !== "object") return "unknown";
  const ms = normaliseMovingState(parsed.moving_state);
  if (ms === "unknown") return "unknown";
  if (!_isValidTimestamp(parsed.updated_at)) return "unknown";
  const age = nowMs - parsed.updated_at;
  if (age < 0 || age > STALE_TTL_MS) return "unknown";
  return ms;
}


function _readPersisted(nowMs = _now()) {
  if (typeof window === "undefined") return "unknown";
  try {
    return _parsePersisted(window.localStorage.getItem(STORAGE_KEY), nowMs);
  } catch (_) {
    return "unknown";
  }
}


function _writePayload(movingState, nowMs = _now()) {
  if (typeof window === "undefined") return;
  const normalised = normaliseMovingState(movingState);
  try {
    if (normalised === "unknown") {
      // Don't persist 'unknown' — the absent-key fallback already
      // resolves to 'unknown', so writing it just adds storage
      // noise. Keeps the legacy-cleanup path simple.
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      const payload = JSON.stringify({
        v:            PAYLOAD_VERSION,
        moving_state: normalised,
        updated_at:   nowMs,
      });
      window.localStorage.setItem(STORAGE_KEY, payload);
    }
  } catch (_) {
    /* storage may be unavailable — caller's state still updates */
  }
  // Same-tab broadcast — must run whether or not localStorage was
  // writable, so sibling hook instances in the SAME tab still hear
  // about the change (private-mode + iOS Safari ITP both block
  // storage events but allow CustomEvents).
  try {
    window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, {
      detail: { moving_state: normalised },
    }));
  } catch (_) {
    /* dispatchEvent never normally fails — defensive only */
  }
}


export default function useDriveModeState() {
  const [movingState, setLocal] = useState(() => _readPersisted());
  // Optional periodic refresh — the moment a payload crosses the
  // 4-hour line we want surfaces to flip to `unknown` without
  // requiring a tab focus event. One-minute tick is calm + cheap.
  const tickRef = useRef(null);

  const setMovingState = useCallback((next) => {
    const normalised = normaliseMovingState(next);
    setLocal(normalised);
    _writePayload(normalised);
  }, []);

  const clearMovingState = useCallback(() => {
    setLocal("unknown");
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
    // Same-tab broadcast so siblings flip to 'unknown' immediately.
    try {
      window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT, {
        detail: { moving_state: "unknown" },
      }));
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onStorage = (e) => {
      if (e?.key !== STORAGE_KEY) return;
      setLocal(_parsePersisted(e.newValue, _now()));
    };
    // Same-tab listener — receives the CustomEvent dispatched by
    // any sibling hook instance's writer. We re-resolve from
    // storage (the authority) rather than trusting the event
    // payload, so any concurrent write is consistent.
    const onSameTab = () => {
      setLocal(_readPersisted());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAME_TAB_EVENT, onSameTab);
    // Calm staleness sweep — every 60s re-resolve from storage. If
    // the timestamp has crossed STALE_TTL_MS the surface flips to
    // `unknown` without a click.
    tickRef.current = window.setInterval(() => {
      setLocal(_readPersisted());
    }, 60_000);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onSameTab);
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
  }, []);

  return {
    movingState,
    setMovingState,
    clearMovingState,
    isManual: movingState !== "unknown",
  };
}

// Exported for tests + reuse by future detection-source modules.
export {
  STORAGE_KEY,
  PAYLOAD_VERSION,
  STALE_TTL_MS,
  SAME_TAB_EVENT,
  _parsePersisted,
  _writePayload,
  _readPersisted,
};
